import React, { useEffect, useMemo, useRef, useState, useTransition } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, IconButton, Paper, Stack, Tab, Tabs } from '@mui/material';

import CopyOfferModal from '~/components/Offers/Edit/CopyOfferModal';

import { CONTENT, IMAGES } from '~/consts/offerFields';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetPackageInclusionsByOffer } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';
import { useGetOffer, useGetOfferSchema } from '~/hooks/apis/offers/useGetOffer';

import { copyImage, getUploadedImageId } from '~/services/ImageService';
import OffersService from '~/services/OffersService';

import { isLTLEHotelOfferType } from '~/utils/offer';

import DefaultContent from './DefaultContent';
import type { CopiedOfferContent } from './DefaultContent/types';
import OfferBuilderImagesVideo from './ImagesVideo';
import Inclusions from './Inclusions';
import { type MasterInclusion, buildMasterInclusionsFromOffer, preserveMasterInclusionOrder } from './Inclusions/utils';
import LazyContentTabPanel from './LazyContentTabPanel';
import OfferPackages from './OfferPackages';
import PropertyInformation, { type OfferBuilderProperty } from './PropertyInformation';
import OfferBuilderRooms from './Rooms';
import { getOfferRoomTypes } from './Rooms/utils';
import TagsAndMarketing from './TagsAndMarketing';
import useOfferBuilderMedia from './useOfferBuilderMedia';

const PROPERTY_INFORMATION_TAB = 'property-information';
const ROOMS_TAB = 'rooms';
const OFFER_PACKAGES_TAB = 'offer-packages';
const INCLUSIONS_TAB = 'inclusions';
const DEFAULT_CONTENT_TAB = 'default-content';
const TAGS_AND_MARKETING_TAB = 'tags-marketing';
const IMAGES_VIDEO_TAB = 'images-video';

const CONTENT_TABS = [
  { label: 'Property information', value: PROPERTY_INFORMATION_TAB },
  { label: 'Rooms', value: ROOMS_TAB },
  { label: 'Offer packages', value: OFFER_PACKAGES_TAB },
  { label: 'Inclusions', value: INCLUSIONS_TAB },
  { label: 'Default content', value: DEFAULT_CONTENT_TAB },
  { label: 'Tags & Marketing', value: TAGS_AND_MARKETING_TAB },
  { label: 'Images & Video', value: IMAGES_VIDEO_TAB },
];

interface CopiedOfferData extends CopiedOfferContent {
  images?: Array<App.OfferImage>;
  packages?: Array<App.Package>;
}

function getInitialContentTab(search: string) {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  // 'contentTab' (by label, e.g. "Rooms") was the param name/format before this was renamed to 'tab' (by slug).
  const legacyLabel = params.get('contentTab');

  return (
    CONTENT_TABS.find((contentTab) => contentTab.value === tab)?.value ??
    CONTENT_TABS.find((contentTab) => contentTab.label === legacyLabel)?.value ??
    DEFAULT_CONTENT_TAB
  );
}

export default function OfferBuilderContentPage() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(() => getInitialContentTab(location.search));
  const [activeContentTab, setActiveContentTab] = useState(() => getInitialContentTab(location.search));
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false);
  const [copiedOfferContent, setCopiedOfferContent] = useState<CopiedOfferContent>();
  const [copiedOfferPackages, setCopiedOfferPackages] = useState<Array<App.Package>>();
  const [packageCopyVersion, setPackageCopyVersion] = useState(0);
  const [savedOfferStatus, setSavedOfferStatus] = useState<string>();
  const [draftMasterInclusions, setDraftMasterInclusions] = useState<Array<MasterInclusion>>([]);
  const [savedMasterInclusions, setSavedMasterInclusions] = useState<Array<MasterInclusion>>([]);
  const hasUserEditedInclusionsRef = useRef(false);
  const initialisedInclusionsOfferIdRef = useRef<string | undefined>(undefined);
  const [, startTabChangeTransition] = useTransition();

  const {
    data: offer,
    isLoading: isLoadingOffer,
    error: offerError,
    refetch: refetchOffer,
  } = useGetOffer(offerId, {
    retain_dynamic_tags: true,
  });
  const { data: offerSchema } = useGetOfferSchema(offerId);
  const isSupportedOfferType = !!offer?.type && isLTLEHotelOfferType(offer.type);
  const hasOfferLoadError = !isLoadingOffer && !!offerError && !offer;
  const shouldRenderContentTabs = isLoadingOffer || (!!offer && isSupportedOfferType);
  const effectivePackages = copiedOfferPackages ?? offer?.packages;
  const propertyId = isSupportedOfferType
    ? effectivePackages?.find((offerPackage) => offerPackage?.fk_property_id)?.fk_property_id
    : undefined;
  const vendorId = offer?.vendor_account_id;
  const currentOfferStatus = savedOfferStatus ?? offer?.status;
  const { data: packageInclusions, isLoading: isLoadingInclusions } = useGetPackageInclusionsByOffer({
    offerId,
    enabled: shouldRenderContentTabs,
  });
  const offerInclusions = useMemo(() => buildMasterInclusionsFromOffer(packageInclusions), [packageInclusions]);

  const {
    data: property,
    isLoading: isLoadingProperty,
    error: propertyError,
  } = useGetProperty<OfferBuilderProperty>({
    propertyId,
    enabled: isSupportedOfferType,
  });

  const hotelName = property?.name || propertyId || 'Property';
  const offerRoomTypes = useMemo(
    () => getOfferRoomTypes(property?.room_types ?? [], effectivePackages ?? []),
    [effectivePackages, property?.room_types],
  );
  const media = useOfferBuilderMedia({
    offerId,
    images: offer?.images,
    propertyName: hotelName,
    propertyRoomTypes: offerRoomTypes,
    videos: offer?.videos,
  });
  const showContentTab = (tab: string) => {
    setSelectedTab(tab);
    startTabChangeTransition(() => setActiveContentTab(tab));

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tab);
    searchParams.delete('contentTab'); // clear the legacy param once a tab change writes the new one
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  const hideCopyModal = () => {
    setIsCopyModalVisible(false);
  };

  useEffect(() => {
    setSavedOfferStatus(offer?.status);
  }, [offer?.status]);

  const copyImagesToOffer = async (images?: Array<App.OfferImage>) => {
    if (!images?.length) {
      return;
    }

    const copiedImages = [];
    for (const image of images) {
      const copyImageResponse = await copyImage(image.id_cloudinary_external);
      const copiedImageId = getUploadedImageId(copyImageResponse);

      if (!copiedImageId) {
        throw new Error('Image copy did not return an ID');
      }

      copiedImages.push({
        filename: image.filename,
        id_cloudinary_external: copiedImageId,
      });
    }

    const existingImageIds = offer?.images?.map((image) => image.id_image) ?? [];

    for (const image of copiedImages) {
      await OffersService.createImage(image, offerId);
    }

    for (const imageId of existingImageIds) {
      await OffersService.deleteImage(offerId, imageId);
    }

    await refetchOffer();
    enqueueSnackbar('Offer images copied', { variant: 'success' });
  };

  const copyOfferData = async (copiedOfferData: CopiedOfferData, selectedCopyOptions: Array<string>) => {
    const nextCopiedOfferContent = { ...copiedOfferData };

    if (selectedCopyOptions.includes(IMAGES)) {
      delete nextCopiedOfferContent.images;
      try {
        await copyImagesToOffer(copiedOfferData.images);
      } catch {
        enqueueSnackbar('Image copy failed. Content and packages were still copied into the draft.', {
          variant: 'warning',
        });
      }
    }

    if (copiedOfferData.packages) {
      setCopiedOfferPackages(copiedOfferData.packages);
      setPackageCopyVersion((version) => version + 1);
      enqueueSnackbar('Copied offer packages into the draft', { variant: 'success' });
    }

    if (selectedCopyOptions.includes(CONTENT)) {
      setCopiedOfferContent(nextCopiedOfferContent);
    }
  };

  useEffect(() => {
    if (isLoadingInclusions) {
      return;
    }

    if (initialisedInclusionsOfferIdRef.current !== offerId) {
      initialisedInclusionsOfferIdRef.current = offerId;
      hasUserEditedInclusionsRef.current = false;
      setDraftMasterInclusions(offerInclusions);
      setSavedMasterInclusions(offerInclusions);
      return;
    }

    // Once editing starts, keep the current row order stable across refetches instead of jumping.
    setSavedMasterInclusions(
      hasUserEditedInclusionsRef.current
        ? (currentInclusions) => preserveMasterInclusionOrder(currentInclusions, offerInclusions)
        : offerInclusions,
    );

    if (!hasUserEditedInclusionsRef.current) {
      setDraftMasterInclusions(offerInclusions);
    }
  }, [isLoadingInclusions, offerId, offerInclusions]);

  const handleMasterInclusionsChange: React.Dispatch<React.SetStateAction<Array<MasterInclusion>>> = (nextValue) => {
    hasUserEditedInclusionsRef.current = true;
    setDraftMasterInclusions(nextValue);
  };

  const handleMasterInclusionsSaved = (nextMasterInclusions: Array<MasterInclusion>) => {
    hasUserEditedInclusionsRef.current = false;
    setDraftMasterInclusions(nextMasterInclusions);
    setSavedMasterInclusions(nextMasterInclusions);
  };

  return (
    <>
      <Helmet>
        <title>Offer Builder | {hotelName}</title>
      </Helmet>

      {hasOfferLoadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to load offer details.
        </Alert>
      )}

      {isLoadingOffer && <Alert severity="info">Loading Offer Builder content.</Alert>}

      {!isLoadingOffer && shouldRenderContentTabs && (
        <>
          <Paper sx={{ position: 'sticky', top: 0, zIndex: 2, mb: 3 }}>
            <Stack direction="row" alignItems="center">
              <Tabs
                value={selectedTab}
                onChange={(_event, value) => showContentTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiTab-root': {
                    minWidth: 'auto',
                    minHeight: 40,
                    px: 1.5,
                    py: 1,
                    fontSize: '0.8125rem',
                  },
                }}
              >
                {CONTENT_TABS.map((tab) => (
                  <Tab key={tab.value} value={tab.value} label={tab.label} />
                ))}
              </Tabs>
              <IconButton
                onClick={() => setIsCopyModalVisible(true)}
                size="large"
                color="primary"
                aria-label="Copy Offer"
              >
                <ContentCopyIcon />
              </IconButton>
            </Stack>
          </Paper>
          <CopyOfferModal
            isCopyModalVisible={isCopyModalVisible}
            setAsNotReady={() => undefined}
            hideCopyModal={hideCopyModal}
            copyOfferData={copyOfferData}
            currentOfferStatus={currentOfferStatus}
            originalOfferDetails={offer}
          />

          <LazyContentTabPanel activeTab={activeContentTab} tab={PROPERTY_INFORMATION_TAB}>
            <PropertyInformation
              property={property}
              vendorId={vendorId}
              isLoadingProperty={isLoadingOffer || isLoadingProperty}
              offerError={offerError}
              propertyError={propertyError}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={ROOMS_TAB}>
            <OfferBuilderRooms
              media={media}
              offerPackages={effectivePackages}
              isLoadingProperty={isLoadingOffer || isLoadingProperty}
              onOpenImagesVideo={() => showContentTab(IMAGES_VIDEO_TAB)}
              propertyError={propertyError}
              propertyId={propertyId}
              propertyName={hotelName}
              propertyRoomTypes={offerRoomTypes}
              vendorId={vendorId}
              offerType={offer?.type}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={OFFER_PACKAGES_TAB}>
            <OfferPackages
              offerId={offerId}
              masterInclusions={isLoadingInclusions ? undefined : savedMasterInclusions}
              packageInclusions={packageInclusions}
              packageCopyVersion={packageCopyVersion}
              packages={effectivePackages}
              property={property}
              propertyRoomTypes={property?.room_types}
              vendorId={vendorId}
              offerType={offer?.type}
              isLoadingOffer={isLoadingOffer}
              offerError={offerError}
              onOpenInclusions={() => showContentTab(INCLUSIONS_TAB)}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={INCLUSIONS_TAB}>
            <Inclusions
              inclusions={draftMasterInclusions}
              isLoadingInclusions={isLoadingInclusions}
              offerId={offerId}
              packageInclusions={packageInclusions}
              packages={effectivePackages}
              savedInclusions={savedMasterInclusions}
              vendorId={vendorId}
              onChange={handleMasterInclusionsChange}
              onSaved={handleMasterInclusionsSaved}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={DEFAULT_CONTENT_TAB}>
            <DefaultContent
              offerAdditionalDescription={offer?.additional_description}
              offerDescription={offer?.description}
              offerFacilities={offer?.facilities}
              offerFinePrint={offer?.fine_print}
              offerTransferDetails={offer?.transfer_details}
              offerFlightDestinationPort={offer?.flight_destination_port}
              offerFlightsEnabled={offer?.flights_enabled}
              offerFlightRegions={offer?.flight_regions}
              offerHighlights={offer?.highlights}
              offerInclusions={offer?.offer_inclusions}
              offerLocation={typeof offer?.location === 'string' ? offer.location : undefined}
              offerPageHighlightTagsOverride={offer?.offer_page_highlight_tags_override}
              offerName={offer?.name}
              offerPreheader={offer?.preheader}
              offerSaleUnit={offer?.sale_unit}
              offerSlug={offer?.slug}
              offerStatus={offer?.status}
              offerSubjectLine={offer?.subject_line}
              offerTileInclusionHeading={offer?.offer_tile_inclusion_heading}
              offerType={offer?.type}
              copiedOfferContent={copiedOfferContent}
              packages={effectivePackages}
              propertyId={propertyId}
              propertyName={property?.name}
              vendorId={vendorId}
              onOfferStatusSaved={setSavedOfferStatus}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={TAGS_AND_MARKETING_TAB}>
            <TagsAndMarketing
              campaigns={offer?.campaigns}
              holidayTypes={offer?.holiday_types}
              locations={offer?.locations}
              offerId={offerId}
              offerSchema={offerSchema}
              offerType={offer?.type}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={IMAGES_VIDEO_TAB}>
            <OfferBuilderImagesVideo
              media={media}
              offerId={offerId}
              offerSlug={offer?.slug}
              offerType={offer?.type}
              propertyId={property?.id}
              propertyLogoId={property?.logo_id_cloudinary_external}
              propertyRoomTypes={offerRoomTypes}
            />
          </LazyContentTabPanel>
        </>
      )}
    </>
  );
}
